import { IAvailableLanguages, LANGUAGE_REGION, REGION_TO_DEFAULT_LANGUAGE } from '@sitegen/common/localization/language-regions';
import { LANGUAGE_TO_REGION_MAP, LANGUAGES_MAP } from '@sitegen/common/localization';

import { IGetSafeLocaleOptions, ILocalePattern, ILocaleRules } from './locale.types';
import { isLocaleValid, standardizeLocale } from 'utils/localizeTime';
import {
    GET_LOCALE_TEXT,
    LANGUAGE_CODE_REGEX,
    LANGUAGE_TO_LOCALE_MAPPING,
    LOCALE_SEPARATOR,
    URL_PARAM_SEPARATOR
} from './locale.constants';
import { DEFAULT_LANGUAGE } from 'constants/locales';

/** Get safe locale based on URL, user input or browser language */
export function getSafeLocale(options: IGetSafeLocaleOptions = {}): string {
    let locale: string | undefined;
    if (options.useUrl) {
        locale = getLocaleFromUrl();
    }

    if (options.language) {
        try {
            locale = getLocaleFromLanguage(options.language);
        } catch {
            locale = undefined;
        }
    }

    /* Use fallback when locale has not been determined */
    if (!locale && options.useBrowserFallback) {
        locale = getLocaleFromBrowserLanguage();
    }

    if (locale && options.useAvailableLocales) {
        locale = filterByAvailableLocales({ locale, availableLocales: options.useAvailableLocales });
    }

    return locale || DEFAULT_LANGUAGE;
}

/**
 * Check if locale is present in URL and is valid
 * If locale is not valid => undefined, if valid but not present in list => DEFAULT_LANGUAGE
 */
export function getLocaleFromUrl(): string | undefined {
    const param: string | undefined = getUnsafeLocaleFromUrlParam();
    if (!param) {
        return;
    }
    return LANGUAGES_MAP[param as string] ? param : DEFAULT_LANGUAGE;
}

/** Get complete locale based on any input, like "en-US", or "de-DE", always return safe value or throw exception */
export function getLocaleFromLanguage(input: string): string {
    const language: string = input.toLowerCase();
    const languageCode: string = language.split(LOCALE_SEPARATOR)[0];

    /* Input string should start with 2 letters with optional info after hyphen */
    if (!languageCode || !LANGUAGE_CODE_REGEX.test(languageCode)) {
        throw new Error(GET_LOCALE_TEXT.LANGUAGE_CODE_LENGTH_ERROR);
    }

    /* If no mapping is provided for given language code, use English */
    if (!LANGUAGE_TO_LOCALE_MAPPING[languageCode]) {
        return DEFAULT_LANGUAGE;
    }

    /* If no optional info is provided use basic language mapping */
    const localeFromLanguage: string = LANGUAGE_TO_LOCALE_MAPPING[languageCode].language;
    if (LANGUAGE_CODE_REGEX.test(language)) {
        return localeFromLanguage;
    }

    const localeFromRule: string | undefined = checkLocaleRules(language, languageCode);
    if (localeFromRule) {
        return localeFromRule;
    }

    const localeFromPattern: string | undefined = checkPatternRules(language, languageCode);
    if (localeFromPattern) {
        return localeFromPattern;
    }

    /* Basic language mapping should always be present */
    return localeFromLanguage;
}

/** If direct locale mapping is present (case-insensitive), use it instantly */
export function checkLocaleRules(language: string, code: string): string | undefined {
    const rule: ILocaleRules = LANGUAGE_TO_LOCALE_MAPPING[code];
    return rule.locales && rule.locales[language] ? rule.locales[language] : undefined;
}

/** Run pattern checks in order, return locale on first match or undefined, if no matches found */
export function checkPatternRules(language: string, code: string): string | undefined {
    const rule: ILocaleRules = LANGUAGE_TO_LOCALE_MAPPING[code];
    const patterns: ILocalePattern[] = rule.patterns || [];

    for (let i: number = 0; i < patterns.length; i++) {
        const pattern: ILocalePattern = patterns[i];
        if (pattern.regex.test(language)) {
            return pattern.locale;
        }
    }

    return;
}

/**
 * Get valid locale param (possibly non-present in list) from URL or undefined
 * TODO: https://gitlab-ncsa.ubisoft.org/Sitegen/sitegen-main/issues/68
 */
export function getUnsafeLocaleFromUrlParam(): string | undefined {
    const params: string[] = window.location.pathname.split(URL_PARAM_SEPARATOR).filter((substring: string): string => substring);
    const locale: string = params[params.length - 1];
    return isLocaleValid(locale) ? standardizeLocale(locale) : undefined;
}

/** Try to get locale from browser settings */
export function getLocaleFromBrowserLanguage(): string | undefined {
    try {
        return getLocaleFromLanguage(window.navigator.language);
    } catch {
        return;
    }
}

/**
 * Check if locale is listed in available languages
 * If locale is valid, but not listed, use region default language
 */
export function filterByAvailableLocales(options: { locale: string, availableLocales: IAvailableLanguages }): string | undefined {
    const region: LANGUAGE_REGION = LANGUAGE_TO_REGION_MAP[options.locale];
    if (!region) {
        return;
    }

    const locale: string | undefined = options.availableLocales[region].find((item: string): boolean => item === options.locale);
    if (locale) {
        return locale;
    }

    return REGION_TO_DEFAULT_LANGUAGE[region];
}
